/**
 * MetricProgressBar — Barra de progreso fina de las tarjetas KPI.
 *
 * QUÉ HACE: pinta un carril con un relleno proporcional (0–100 %) del color o
 *   degradado indicado; el ancho se anima al montar y en cada cambio de valor
 *   (subida/bajada). La usa la prop `progress` de MetricCard y la escalera
 *   `MetricLadder` (con degradado).
 * QUÉ INVOCA: `useAnimatedNumber` (conteo animado del ancho).
 * QUÉ EXPORTA: MetricProgressBar.
 * QUÉ NO DEBE HACER: no calcula el porcentaje final ni accede a datos.
 */

import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";

interface MetricProgressBarProps {
  /** Porcentaje de avance 0–100 (se recorta al rango). */
  value: number;
  /** Clases Tailwind del relleno (color o degradado). Por defecto azul. */
  fillClassName?: string;
  /** Clases Tailwind del alto del carril. Por defecto `h-2`. */
  heightClassName?: string;
  /** Etiqueta centrada **dentro** de la barra (p. ej. "42%"). */
  label?: string;
  className?: string;
}

export const MetricProgressBar = ({
  value,
  fillClassName = "bg-blue-500",
  heightClassName = "h-2",
  label,
  className = "",
}: MetricProgressBarProps) => {
  const clamped = Math.max(0, Math.min(100, value));
  const { value: animated } = useAnimatedNumber(clamped, { decimals: 1 });
  const animatedWidth = Math.max(0, Math.min(100, animated));

  return (
    <div
      className={`${heightClassName} relative overflow-hidden rounded-full bg-slate-100 ring-1 ring-inset ring-slate-200/70 dark:bg-slate-700/50 dark:ring-slate-600/40 ${className}`}
    >
      <div
        className={`h-full rounded-full shadow-sm transition-all ${fillClassName}`}
        style={{ width: `${animatedWidth}%` }}
      />
      {label && (
        <span
          className={`absolute inset-0 flex items-center justify-center text-[6px] font-semibold leading-none tabular-nums sm:text-[7px] ${
            clamped >= 50 ? "text-white" : "text-gray-600 dark:text-gray-200"
          }`}
        >
          {label}
        </span>
      )}
    </div>
  );
};
